import React from "react";
import { motion } from "framer-motion";
import { Info } from "lucide-react";
import { useLang } from "../../context/LangContext";
import { translations, pickBilingual } from "../../lib/i18n";
import { TEST_IDS } from "../../constants/testIds";

const initialsOf = (name = "") =>
  name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((n) => n[0])
    .join("")
    .toUpperCase();

const ReferenceCard = ({ r, i, lang, offset = 0 }) => (
  <motion.article
    key={r.id || r.name}
    data-testid={TEST_IDS.references.card(offset + i)}
    initial={{ opacity: 0, y: 16 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-40px" }}
    transition={{ duration: 0.5, delay: (i % 4) * 0.05 }}
    className="rp-card p-5 group"
  >
    <div className="rp-avatar mb-4">
      {r.photo_url ? (
        <img
          src={r.photo_url}
          alt={r.name}
          className="w-full h-full object-cover object-top"
          loading="lazy"
        />
      ) : (
        <span>{initialsOf(r.name)}</span>
      )}
    </div>
    <div className="flex items-center gap-2 mb-2">
      <span className="font-mono text-[10px] text-[color:var(--rp-muted)]">
        {String(offset + i + 1).padStart(2, "0")}
      </span>
      <span className="w-1 h-1 bg-[color:var(--rp-accent)]" />
    </div>
    <h3 className="font-display font-bold text-white text-lg leading-tight tracking-tight group-hover:text-[color:var(--rp-accent)] transition-colors">
      {r.name}
    </h3>
    <div className="mt-2 text-[10px] tracking-[0.2em] uppercase text-[color:var(--rp-muted)]">
      {pickBilingual(r.role, lang)}
    </div>
  </motion.article>
);

const ReferenceGroup = ({ label, items, lang, offset = 0 }) => (
  <div>
    <div className="text-[10px] tracking-[0.3em] uppercase text-[color:var(--rp-accent)] mb-6">
      {label}
    </div>
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
      {items.map((r, i) => (
        <ReferenceCard key={r.id || r.name} r={r} i={i} lang={lang} offset={offset} />
      ))}
    </div>
  </div>
);

const References = ({ references = [] }) => {
  const { lang } = useLang();
  const t = translations[lang].references;

  const coaches = references.filter((r) => r.category === "coach");
  const management = references.filter((r) => r.category === "management");

  return (
    <section
      id="references"
      data-testid={TEST_IDS.references.root}
      className="rp-section bg-[color:var(--rp-bg)]"
    >
      <div className="rp-container">
        <div className="mb-14 grid grid-cols-12 gap-8">
          <div className="col-span-12 lg:col-span-6">
            <div className="rp-eyebrow mb-4">09 · {t.eyebrow}</div>
            <h2 className="rp-heading rp-headline">{t.title}</h2>
          </div>
          <div className="col-span-12 lg:col-span-6 flex items-end">
            <div className="flex gap-3 items-start text-[color:var(--rp-muted)] text-[13px] leading-relaxed max-w-[52ch]">
              <Info size={16} className="flex-shrink-0 mt-0.5 text-[color:var(--rp-accent)]" />
              <p>{t.privacy}</p>
            </div>
          </div>
        </div>

        <div className="space-y-14">
          <ReferenceGroup label={t.groups.coach} items={coaches} lang={lang} offset={0} />
          <ReferenceGroup label={t.groups.management} items={management} lang={lang} offset={coaches.length} />
        </div>
      </div>
    </section>
  );
};

export default References;
